import { openDB, IDBPDatabase } from 'idb';
import { encryptData, decryptData } from './encryption';

const DB_NAME = 'hypnotic_app_data';
const DB_VERSION = 3; // Incremented for schema standardization

const STORES = {
  AUDIO: 'audio_blobs',
  SAVES: 'simulation_saves',
  MELODIES: 'user_melodies'
} as const;

class LocalPersistentStore {
  private dbPromise: Promise<IDBPDatabase> | null = null;

  private getDB() {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Delete old stores if they exist with wrong keyPath
        if (db.objectStoreNames.contains(STORES.AUDIO)) {
          db.deleteObjectStore(STORES.AUDIO);
        }
        if (db.objectStoreNames.contains(STORES.SAVES)) {
          db.deleteObjectStore(STORES.SAVES);
        }
        if (db.objectStoreNames.contains(STORES.MELODIES)) {
          db.deleteObjectStore(STORES.MELODIES);
        }

        // Create with standardized 'id' keyPath
        db.createObjectStore(STORES.AUDIO, { keyPath: 'id' });
        db.createObjectStore(STORES.SAVES, { keyPath: 'id' });
        db.createObjectStore(STORES.MELODIES, { keyPath: 'id' });
      },
    });
    return this.dbPromise;
  }

  /**
   * Generates a unique key from a URL by stripping tokens
   */
  private getCacheKey(url: string): string {
    try {
      if (typeof url !== 'string') return String(url);
      if (url.startsWith('data:')) return url.substring(0, 100);
      const u = new URL(url);
      return u.origin + u.pathname;
    } catch (e) {
      return url;
    }
  }

  // --- Audio Methods (Legacy Compatibility) ---
  async get(url: string): Promise<ArrayBuffer | null> {
    const key = this.getCacheKey(url);
    const db = await this.getDB();
    const entry = await db.get(STORES.AUDIO, key);
    return entry ? entry.data : null;
  }

  async set(url: string, data: ArrayBuffer): Promise<void> {
    const key = this.getCacheKey(url);
    const db = await this.getDB();
    await db.put(STORES.AUDIO, {
      id: key,
      data,
      timestamp: Date.now()
    });
  }

  // --- Generic JSON Support ---
  async getJson<T>(store: keyof typeof STORES, id: string): Promise<T | null> {
    const db = await this.getDB();
    const entry = await db.get(STORES[store], id);
    if (!entry) return null;

    // Use decryption for SAVES and MELODIES
    if (store === 'SAVES' || store === 'MELODIES') {
      if (typeof entry.data === 'string') {
        const decrypted = decryptData(entry.data);
        try {
          return JSON.parse(decrypted) as T;
        } catch (e) {
          // If parsing fails, it might be legacy unencrypted data or actually a string
          try {
             return JSON.parse(entry.data) as T;
          } catch(e2) {
             return entry.data as unknown as T;
          }
        }
      }
    }

    return entry.data;
  }

  async setJson<T>(store: keyof typeof STORES, id: string, data: T): Promise<void> {
    const db = await this.getDB();
    let finalData: any = data;

    // Encrypt for SAVES and MELODIES
    if (store === 'SAVES' || store === 'MELODIES') {
      const stringified = JSON.stringify(data);
      finalData = encryptData(stringified);
    }

    await db.put(STORES[store], {
      id,
      data: finalData,
      timestamp: Date.now()
    });
  }

  /**
   * Safe migration from localStorage to IndexedDB
   */
  async migrateFromLocalStorage<T>(store: keyof typeof STORES, key: string, id: string): Promise<T | null> {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    try {
      // Check if raw is already encrypted
      let decrypted = raw;
      if (!raw.startsWith('{') && !raw.startsWith('[')) {
         decrypted = decryptData(raw);
      }
      
      const parsed = JSON.parse(decrypted);
      await this.setJson(store, id, parsed);
      window.localStorage.removeItem(key);
      return parsed as T;
    } catch (e) {
      return null;
    }
  }

  async delete(store: keyof typeof STORES, id: string): Promise<void> {
    const key = id.startsWith('http') ? this.getCacheKey(id) : id;
    const db = await this.getDB();
    await db.delete(STORES[store], key);
  }

  async clear(): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(Object.values(STORES), 'readwrite');
    await Promise.all(Object.values(STORES).map(s => tx.objectStore(s).clear()));
    await tx.done;
  }
}

export const persistentStore = new LocalPersistentStore();
// Alias for compatibility
export const audioCache = persistentStore;
